import { inject } from '@angular/core';
import { Router, type CanActivateFn, type UrlTree } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Authentication guard for protected routes.
 * 
 * Checks if the user is authenticated before allowing access to a route.
 * If not authenticated, redirects to the login page with the intended URL
 * stored in query parameters for post-login redirect.
 * 
 * @remarks
 * This guard relies on the app initializer (initializeAuth) to have already
 * checked the backend session status before any routing occurs. This ensures
 * that the authentication state is properly restored from the stored token
 * before this guard runs.
 * 
 * The guard simply checks the signal state - it does NOT make additional
 * backend calls, as that would cause unnecessary delays and duplicate requests.
 * 
 * @param route - The activated route snapshot
 * @param state - The router state snapshot
 * @returns True if authenticated, UrlTree to login page if not
 */
export const authGuard: CanActivateFn = (route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check authentication status from signal
  // The app initializer has already checked the backend session
  const isAuthenticated = authService.isAuthenticated();
  
  console.log('🛡️ Auth Guard:', {
    isAuthenticated,
    currentUser: authService.currentUser(),
    requestedUrl: state.url
  });

  if (isAuthenticated) {
    return true;
  }

  // Not authenticated - redirect to login with return URL
  console.warn('Auth Guard: User not authenticated, redirecting to login');
  return router.createUrlTree(['/login'], {
    queryParams: { returnUrl: state.url }
  });
};
